import { FC, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, Grid, CircularProgress, IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { moneyCL } from "../../../toolbox/helpers/money-helpers";
import { useRenditionBuildingStore } from "../../../hooks/useRenditionBuildingStore";
import CustomTable from "../../../components/common/CustomTable/CustomTable";

interface ModalBuildingDepartmentProps {
    open: boolean;
    closeModal: () => void;
    renditionSelected: { id: string } | null;
}
const columns = [
    { type: "text", field: "description", label: "DESCRIPCION", align: "center" },
    { type: "text", field: "building", label: "EDIFICIO", align: "center" },
    {
        type: "text",
        field: "previous_month_balance",
        label: "DEUDA DEL MES ANTERIOR",
        align: "center",
        format: (row) => moneyCL(row.previous_month_balance),
    },
    { type: "text", field: "alicuota", label: "ALICUOTA", align: "center", format: (row) => `${row.alicuota} %` },
    {
        type: "text", field: "alicuota", label: "MONTO ALICUOTA", align: "center",
        format: (row) => moneyCL(Math.ceil(row.alicuota * row.total_general_spents / 100))
    },
    {
        type: "text",
        field: "favor_month_balance",
        label: "SALDO A FAVOR",
        align: "center",
        format: (row) => moneyCL(row.favor_month_balance),
    },
    {
        type: "text",
        field: "amount_individual",
        label: "TOTAL INDIVIDUAL",
        align: "center",
        format: (row) => moneyCL(row.amount_individual),
    },
    {
        type: "text",
        field: "amount_mes",
        label: "DEUDA DEL MES",
        align: "center",
        format: (row) => moneyCL(row.amount_mes),
    },
    {
        type: "text",
        field: "total_pay",
        label: "TOTAL A PAGAR",
        align: "center",
        format: (row) => moneyCL(row.total_pay),
    },
    { type: "detail", field: "detail", label: "", align: "center" },
];

export const ModalBuildingDepartment: FC<ModalBuildingDepartmentProps> = ({
    open,
    closeModal,
    renditionSelected
}) => {
    const { getDetailRendicionAll, getRenditionDetailPDF, renditionBuildings } = useRenditionBuildingStore();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [formattedData, setFormattedData] = useState<any[]>([]);

    useEffect(() => {
        if (renditionSelected?.id) {
            getDataDepartments(renditionSelected.id);
        }
    }, [renditionSelected]);

    const formatRenditionData = (dataArray) => {
        if (!Array.isArray(dataArray)) return [];

        return dataArray.map((data) => ({
            description: data.department.department_name,
            building: data.department.building,
            subtotal: data.data.general.total,
            amount_individual: data.data.individual.total,
            amount_mes: data.data.subtotal,
            previous_month_balance: data.data.saldoMesAnterior,
            favor_month_balance: data.data.saldoFavorMesAnterior,
            total_pay: data.data.total_pagar,
            total_paid: data.data.total_pagado,
            iddepartment: data.department.iddepartment,
            amount_balance: data.data.balanceMes,
            alicuota: parseFloat(data.data.alicuota),
            total_building: parseInt(data.data.edificio.total),
            total_general_spents: data.data.total_general_spents
        }));
    };

    const getDataDepartments = async (id) => {
        try {
            setLoading(true);
            const response = await getDetailRendicionAll({ idreport_building: id });

            if (response && Array.isArray(response)) {
                const formatted = formatRenditionData(response);
                setFormattedData(formatted);
            }
        } catch (error) {
            console.error("Error al obtener la rendición:", error);
        } finally {
            setLoading(false);
        }
    };

    const onHelper = async (rowSelected) => {
        setDownloading(true);
        try {
            const parsedId = renditionSelected?.id ? parseInt(renditionSelected.id, 10) : 0;
            const response = await getRenditionDetailPDF(parsedId, rowSelected.iddepartment);
            if (response !== false) {
                const blob = new Blob([response], { type: "application/pdf" });
                const bloblURL = URL.createObjectURL(blob);
                window.open(bloblURL, "_blank");
            }
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={closeModal} maxWidth="lg" fullWidth>
            <DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={closeModal}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 200 }}>
                        <CircularProgress />
                    </Grid>
                ) : (<>
                    <Grid item container sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <Typography variant='h6' component='h1' gutterBottom sx={{ mt: 1, ml: 1 }} className='title-main'>
                            RENDICIONES DE DEPARTAMENTOS
                        </Typography>
                    </Grid>

                    <Grid
                        container
                        spacing={2}
                        justifyContent="center"
                        sx={{ mb: 2 }}
                    >
                        <Grid item xs={12} sm={4}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    boxShadow: 1,
                                }}
                            >
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total gastos generales
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {moneyCL(renditionBuildings[0]?.amount_general || 0)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    boxShadow: 1,
                                }}
                            >
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total gastos individuales
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {moneyCL(renditionBuildings[0]?.amount_individual || 0)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    boxShadow: 1,
                                }}
                            >
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total general
                                </Typography>
                                <Typography variant="h6" color="primary">
                                    {moneyCL(renditionBuildings[0]?.amount_total || 0)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <CustomTable
                            columns={columns}
                            rows={formattedData}
                            onRowClick={() => { }}
                            loading={downloading}
                            hasOptions
                            onHelper={onHelper}
                            icon_helper={<PictureAsPdfIcon fontSize="small" />}
                            tooltip_helper={"Ver PDF"}
                        />
                    </Grid>
                </>)}
            </DialogContent>
        </Dialog>
    );
};
